/**
 * Resolving a workflow parameter's OBJECT type against the Kuika Data Types catalog.
 *
 * ## Why this exists
 *
 * KOA-15840 gave a process parameter a second shape: besides the nine scalar types the
 * engine supports, it can now reference a catalog entry and be emitted as a typed POCO
 * (single, or a list). The DSL used to refuse the whole idea — "Object tipi
 * desteklenmiyor" — because there was no catalog to reference and a fabricated type name
 * produced a class the generator never emits. That reason is gone; the refusal is not
 * automatically right any more.
 *
 * What has NOT changed is the cost of getting the name wrong. The generator trusts the
 * reference: a dangling one emits a reference to a class that is never generated, and a
 * blank one emits a bare namespace. Neither survives compilation. KOA-15840 added backend
 * validators that block generation for exactly this, which means a wrong name is caught —
 * but only after the process is built, at publish time, far from the line that caused it.
 *
 * So the rule here is: a catalog type is addressed BY NAME, the name must already exist,
 * and a name that does not resolve is an error that LISTS what does. Never coerce, never
 * invent, never create the type as a side effect — authoring the catalog is the data-type
 * manager's job, and a process that silently defines its own vocabulary is how two
 * spellings of the same entity end up in one project.
 *
 * Matching is case-insensitive because the author types the name and the catalog stores
 * it; it uses plain `toLowerCase()` (never `toLocaleLowerCase()`) so a Turkish keyboard
 * cannot make "Izin" and "izin" stop matching.
 */

export interface ICatalogTypeResolved {
  ok: true;
  /** The catalog's OWN spelling — what gets persisted, so a rename cascade can find it. */
  name: string;
}

export interface ICatalogTypeUnresolved {
  ok: false;
  message: string;
}

export type CatalogTypeResolution = ICatalogTypeResolved | ICatalogTypeUnresolved;

/** How many names an error message lists before it stops being readable. */
const MAX_LISTED_NAMES: number = 30;

/**
 * Resolve a requested object-type name against the names the project's catalog carries.
 *
 * `availableNames` is what `sd.types` holds — the datasource-derived types PLUS the
 * user-managed catalog, which the project provider merges by name. Passing them in keeps
 * this decision pure and testable; the caller owns the reading.
 */
export function resolveCatalogTypeName(
  requested: string | undefined | null,
  availableNames: readonly string[]
): CatalogTypeResolution {
  const trimmed: string = (requested ?? "").trim();
  if (trimmed.length === 0) {
    return {
      ok: false,
      message:
        "An OBJECT parameter needs a Kuika Data Type name: `PARAM BO <name> OBJECT <TypeName> [LIST]`. " +
        "Define the type in the data-type manager first — the DSL never creates one."
    };
  }

  const wanted: string = trimmed.toLowerCase();
  const match: string | undefined = availableNames.find(
    (name: string): boolean => typeof name === "string" && name.trim().toLowerCase() === wanted
  );
  if (match !== undefined) {
    return { ok: true, name: match.trim() };
  }

  // An empty catalog and a mistyped name are different problems and get different advice.
  if (availableNames.length === 0) {
    return {
      ok: false,
      message:
        `Object type "${trimmed}" cannot be used: this project has no data types to reference yet. ` +
        `Define it in the data-type manager first, then reference it by name. ` +
        `Until then use scalar parameters — the DSL never creates a type on your behalf.`
    };
  }

  const listed: string[] = [...availableNames]
    .filter((name: string): boolean => typeof name === "string" && name.trim().length > 0)
    .map((name: string): string => name.trim())
    .sort((a: string, b: string): number => a.localeCompare(b, "en", { sensitivity: "base" }));
  const shown: string = listed.slice(0, MAX_LISTED_NAMES).join(", ");
  const more: string = listed.length > MAX_LISTED_NAMES ? `, … (${listed.length - MAX_LISTED_NAMES} more)` : "";

  return {
    ok: false,
    message:
      `Unknown object type "${trimmed}". A \`PARAM BO … OBJECT\` references an EXISTING Kuika Data Type by ` +
      `name — the DSL never creates one, and a name that does not exist emits a reference to a class the ` +
      `generator never writes, so the app stops compiling. Available: ${shown}${more}.`
  };
}
